import {
  COYOTE_TICKS,
  DASH_COOLDOWN_TICKS,
  DASH_SPEED,
  DASH_TICKS,
  DT,
  GRAVITY,
  JUMP_BUFFER_TICKS,
  JUMP_CUTOFF,
  JUMP_VELOCITY,
  MAX_FALL_SPEED,
  MAX_RUN_SPEED,
  PLAYER_H,
  PLAYER_W,
  RUN_ACCEL,
  RUN_DECEL,
  TILE,
  WALKER_SPEED,
} from './constants';
import { isSolid, type Level } from './level';
import { moveBody, type Body } from './movement';
import { defaultMods, type PlayerMods } from './powerups';
import type { Vec2 } from './types';

export type PlayerVisual = 'idle' | 'run' | 'jump' | 'fall' | 'dash';

export interface Player {
  body: Body;
  facing: 1 | -1;
  onGround: boolean;
  coyote: number;
  jumpBuffer: number;
  dashTicks: number;
  dashCooldown: number;
  airJumps: number; // remaining mid-air jumps (double-jump power-up)
  iframes: number; // invincibility ticks remaining (after armor absorbs a hit)
  visual: PlayerVisual;
}

/** Edge-resolved input for the player, produced by the World from raw InputState. */
export interface PlayerInput {
  left: boolean;
  right: boolean;
  jumpHeld: boolean;
  jumpPressed: boolean;
  jumpReleased: boolean;
  dashPressed: boolean;
}

export function createPlayer(spawn: Vec2): Player {
  return {
    body: { x: spawn.x, y: spawn.y, w: PLAYER_W, h: PLAYER_H, vx: 0, vy: 0 },
    facing: 1,
    onGround: false,
    coyote: 0,
    jumpBuffer: 0,
    dashTicks: 0,
    dashCooldown: 0,
    airJumps: 0,
    iframes: 0,
    visual: 'idle',
  };
}

export function updatePlayer(
  p: Player,
  input: PlayerInput,
  level: Level,
  mods: PlayerMods = defaultMods(),
): void {
  // Coyote time + air-jump refill while grounded.
  if (p.onGround) {
    p.coyote = COYOTE_TICKS;
    p.airJumps = mods.doubleJump ? 1 : 0;
  } else if (p.coyote > 0) {
    p.coyote--;
  }

  // Jump buffer: a press is remembered briefly so it fires on landing.
  if (input.jumpPressed) p.jumpBuffer = JUMP_BUFFER_TICKS;
  else if (p.jumpBuffer > 0) p.jumpBuffer--;

  if (p.dashCooldown > 0) p.dashCooldown--;

  // Begin a dash (air or ground) if available.
  if (input.dashPressed && p.dashTicks <= 0 && p.dashCooldown <= 0) {
    p.dashTicks = DASH_TICKS;
  }

  const dashing = p.dashTicks > 0;
  if (dashing) {
    // Flat dash: forced horizontal speed, gravity suspended.
    p.body.vx = p.facing * DASH_SPEED * mods.dashMult;
    p.body.vy = 0;
    p.dashTicks--;
    if (p.dashTicks === 0) p.dashCooldown = DASH_COOLDOWN_TICKS;
  } else {
    const maxRun = MAX_RUN_SPEED * mods.runMult;
    const dir = (input.right ? 1 : 0) - (input.left ? 1 : 0);
    if (dir !== 0) {
      p.facing = dir as 1 | -1;
      p.body.vx += dir * RUN_ACCEL * DT;
    } else {
      const dec = RUN_DECEL * DT;
      if (p.body.vx > 0) p.body.vx = Math.max(0, p.body.vx - dec);
      else if (p.body.vx < 0) p.body.vx = Math.min(0, p.body.vx + dec);
    }
    if (p.body.vx > maxRun) p.body.vx = maxRun;
    if (p.body.vx < -maxRun) p.body.vx = -maxRun;

    p.body.vy += GRAVITY * DT;
    if (p.body.vy > MAX_FALL_SPEED) p.body.vy = MAX_FALL_SPEED;

    const jumpVel = JUMP_VELOCITY * mods.jumpMult;
    // Buffered ground/coyote jump.
    if (p.jumpBuffer > 0 && p.coyote > 0) {
      p.body.vy = jumpVel;
      p.coyote = 0;
      p.jumpBuffer = 0;
    } else if (input.jumpPressed && p.coyote <= 0 && !p.onGround && p.airJumps > 0) {
      // Mid-air (double) jump on a fresh press.
      p.body.vy = jumpVel;
      p.airJumps--;
    }
    // Variable-height jump: releasing while rising cuts the ascent once.
    if (input.jumpReleased && p.body.vy < 0) {
      p.body.vy *= JUMP_CUTOFF;
    }
  }

  const col = moveBody(p.body, level);
  p.onGround = col.onGround;
  p.visual = computeVisual(p, dashing);
}

function computeVisual(p: Player, dashing: boolean): PlayerVisual {
  if (dashing) return 'dash';
  if (!p.onGround) return p.body.vy < 0 ? 'jump' : 'fall';
  return Math.abs(p.body.vx) > 1 ? 'run' : 'idle';
}

export interface Walker {
  body: Body;
  dir: 1 | -1;
  onGround: boolean;
  speed: number;
}

export function createWalker(spawn: Vec2, speed: number = WALKER_SPEED): Walker {
  // Walkers are one tile square.
  return {
    body: { x: spawn.x, y: spawn.y, w: TILE, h: TILE, vx: 0, vy: 0 },
    dir: -1,
    onGround: false,
    speed,
  };
}

export function updateWalker(w: Walker, level: Level): void {
  w.body.vy += GRAVITY * DT;
  if (w.body.vy > MAX_FALL_SPEED) w.body.vy = MAX_FALL_SPEED;

  // Turn around at a ledge edge so walkers never walk off platforms.
  if (w.onGround) {
    const aheadX = w.dir > 0 ? w.body.x + w.body.w + 1 : w.body.x - 1;
    const footTy = Math.floor((w.body.y + w.body.h + 1) / TILE);
    const aheadTx = Math.floor(aheadX / TILE);
    if (!isSolid(level, aheadTx, footTy)) w.dir = w.dir === 1 ? -1 : 1;
  }

  w.body.vx = w.dir * w.speed;
  const col = moveBody(w.body, level);
  if (col.hitWall) w.dir = w.dir === 1 ? -1 : 1;
  w.onGround = col.onGround;
}
